import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createArticle, updateArticle, getArticle } from '../services/adminApi';

const ArticleEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [model, setModel] = useState<any>({
    title: '',
    slug: '',
    category: '',
    summary: '',
    content: '',
    tags: '',
    publishedAt: new Date().toISOString().slice(0, 10),
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const data = await getArticle(id);
        setModel({
          title: data.title,
          slug: data.slug,
          category: data.category,
          summary: data.summary,
          content: data.content,
          tags: (data.tags || []).join(','),
          publishedAt: new Date(data.publishedAt).toISOString().slice(0, 10),
        });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('articleEditor.loadFailed'));
      }
    })();
  }, [id, t]);

  const onChange = (key: string, value: string) => setModel((m: any) => ({ ...m, [key]: value }));

  const handleSave = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);

    const payload = {
      title: model.title,
      slug: model.slug,
      category: model.category,
      summary: model.summary,
      content: model.content,
      tags: model.tags.split(',').map((t: string) => t.trim()).filter(Boolean),
      publishedAt: new Date(model.publishedAt).toISOString(),
    };

    try {
      if (id) {
        await updateArticle(id, payload);
      } else {
        await createArticle(payload);
      }
      navigate('/admin/articles');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('articleEditor.saveFailed'));
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{id ? t('articleEditor.titleEdit') : t('articleEditor.titleNew')}</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="form-grid" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="article-title">{t('articleEditor.fieldTitle')}</label>
          <input
            id="article-title"
            className="form-control"
            value={model.title}
            onChange={(e) => onChange('title', e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-slug">{t('articleEditor.fieldSlug')}</label>
          <input
            id="article-slug"
            className="form-control"
            value={model.slug}
            onChange={(e) => onChange('slug', e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-category">{t('articleEditor.fieldCategory')}</label>
          <input
            id="article-category"
            className="form-control"
            value={model.category}
            onChange={(e) => onChange('category', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-summary">{t('articleEditor.fieldSummary')}</label>
          <textarea
            id="article-summary"
            className="form-control"
            rows={3}
            value={model.summary}
            onChange={(e) => onChange('summary', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-content">{t('articleEditor.fieldContent')}</label>
          <textarea
            id="article-content"
            className="form-control"
            rows={10}
            value={model.content}
            onChange={(e) => onChange('content', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-tags">{t('articleEditor.fieldTags')}</label>
          <input
            id="article-tags"
            className="form-control"
            value={model.tags}
            onChange={(e) => onChange('tags', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="article-publishedAt">{t('articleEditor.fieldPublishedAt')}</label>
          <input
            id="article-publishedAt"
            type="date"
            className="form-control"
            value={model.publishedAt}
            onChange={(e) => onChange('publishedAt', e.target.value)}
          />
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" type="submit">
            {t('articleEditor.save')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/admin/articles')}>
            {t('articleEditor.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ArticleEditor;
